import React, { useState } from "react";
import {
    Box,
    Card,
    Grid,
    Stack,
    Typography,
    IconButton,
    Chip,
    Button,
    LinearProgress,
} from "@mui/material";
import Iconify from "./Iconify";


// helpers locaux pour les couleurs
const hexToRgba = (hex, opacity) => {
    if (!hex) return `rgba(94, 23, 235, ${opacity})`; // fallback violet
    const clean = hex.replace("#", "");
    const bigint = parseInt(clean, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const getContrastColor = (hex = "#ffffff") => {
    const color = hex.replace("#", "");
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128 ? "#FFFFFF" : "#000000";
};

export default function TicketItemCompact({
    ticket,
    index,
    quantite,
    addTicket,
    removeTicket,
    quota = 0,
    sell = 0,
    encours = 0,
    showQuota = true,
    config,
}) {
    const data = ticket.data();
    const price = data.price || 0;
    const isFree = price === 0;

    const [expanded, setExpanded] = useState(false);
    const MAX_CHARS = 68;

    const description = data.description || "";
    const isLongDesc = description.length > MAX_CHARS;
    const displayDesc =
        expanded || !isLongDesc
            ? description
            : description.slice(0, MAX_CHARS) + "…";

    const used = sell + encours;
    const remaining = Math.max(quota - used, 0);
    const progress = quota > 0 ? (used / quota) * 100 : 0;

    const quotaAvailable = quota > 0 ? Math.max(quota - used, 0) : null;
    const isSoldOut = quotaAvailable !== null && quotaAvailable <= 0;

    // couleurs widget
    const primaryHex = config?.color?.primaryColor
        ? `#${config.color.primaryColor}`
        : "#5E17EB";
    const secondaryHex = config?.color?.secondaryColor
        ? `#${config.color.secondaryColor}`
        : "#f2f2f2";
    const textHex = config?.color?.textColor
        ? `#${config.color.textColor}`
        : "#1F1F1F";

    const bgCard = hexToRgba(primaryHex, 0.08);
    const btnTextColor = getContrastColor(primaryHex);

    return (
        <Card
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${secondaryHex}`,
                bgcolor: bgCard,
                color: textHex,
            }}
        >
            <Grid container spacing={1.5}>
                {/* Colonne gauche : infos billet */}
                <Grid item xs={8} sx={{ display:'flex', flexDirection:'column', }}>
                    <Stack spacing={0.6} >
                        <Stack
                            direction="row"
                            spacing={0.8}
                            // alignItems="center"
                            flexWrap="wrap"
                        >
                            <Typography
                                fontSize={15}
                                fontWeight={500}
                                sx={{ color: textHex }}
                            >
                                {data.ticket_name}
                            </Typography>

                        </Stack>
                        

                        {/* Description courte */}
                        {description && (
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#6B7280",
                                        fontSize: 13,
                                        whiteSpace: "pre-line",
                                    }}
                                >
                                    {displayDesc}
                                </Typography>
                                {isLongDesc && (
                                    <Button
                                        size="small"
                                        onClick={() => setExpanded((prev) => !prev)}
                                        sx={{
                                            color: primaryHex,
                                            textTransform: "none",
                                            fontWeight: 500,
                                            mt: 0.2,
                                            px: 0,
                                            fontSize: 11,
                                            "&:hover": {
                                                background: "transparent",
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {expanded ? "Voir moins" : "Voir plus"}
                                    </Button>
                                )}
                            </Box>
                        )}

                        <Typography
                        fontSize={15}
                        fontWeight={500}
                        sx={{ color: isFree ? "#059669" : textHex }}
                    >
                        {isFree ? "Gratuit" : `${price.toLocaleString("fr-FR")} CFA`}
                    </Typography>

                        {/* quota / stock (géré via props, donc compatible multi-séances) */}
                        {showQuota && quota > 0 && (
                            <Stack spacing={0.5}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                        {remaining > 0
                                            ? `Plus que ${remaining} billet${remaining > 1 ? "s" : ""}`
                                            : "Plus de billet disponible"}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                        {used}/{quota} vendus
                                    </Typography>
                                </Stack>

                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        height: 6,
                                        borderRadius: 999,
                                        bgcolor: "rgba(148, 163, 184, 0.25)",
                                        "& .MuiLinearProgress-bar": { borderRadius: 999 },
                                    }}
                                />
                            </Stack>
                        )}
                    </Stack>
                </Grid>

                {/* Colonne droite : prix + quantité */}
                <Grid
                    item
                    xs={4}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        gap: 0.5,
                    }}
                >

                    {/* + / - ou épuisé */}
                    {isSoldOut ? (
                        <Chip
                            label="Épuisé"
                            sx={{
                                borderRadius: "999px",
                                bgcolor: "#FBE79D",
                                border: "1px dashed #FACC15",
                                color: "#92400E",
                                fontSize: 10,
                                fontWeight: 600,
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                mt: 0.5,
                                borderRadius: 999,
                                bgcolor: "#F9FAFB",
                                border: "1px solid #E5E7EB",
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <IconButton
                                size="small"
                                disabled={quantite[index] === 0}
                                onClick={() => removeTicket(index)}
                                sx={{
                                    p: 0.3,
                                    bgcolor:
                                        quantite[index] === 0
                                            ? "transparent"
                                            : hexToRgba(primaryHex, 0.1),
                                    color: quantite[index] === 0 ? "#9CA3AF" : primaryHex,
                                }}
                            >
                                <Iconify icon="ic:baseline-minus" width={25} height={25} />
                            </IconButton>

                            <Typography
                                fontSize={14}
                                fontWeight={600}
                                sx={{ px: 1, minWidth: 20, textAlign: "center" }}
                            >
                                {quantite[index]}
                            </Typography>

                            <IconButton
                                size="small"
                                onClick={() => addTicket(index)}
                                sx={{
                                    p: 0.3,
                                    bgcolor: primaryHex,
                                    color: btnTextColor,
                                }}
                            >
                                <Iconify icon="material-symbols:add" width={25} height={25} />
                            </IconButton>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Card>
    );
}
